<script>
import { Helper } from '@/mixin/helper'
import McGraphqlEditableTable from '@/components/McGraphqlEditableTable'
import {
  GET_DEPARTMENT,
  DEPARTMENT_MUTATION_DELETE,
} from '@/services/api/setting/employee/department'
import {
  UNIT_MUTATION_CREATE,
  UNIT_MUTATION_UPDATE,
  GET_UNIT,
} from '@/services/api/setting/employee/unit'

export default {
  components: { McGraphqlEditableTable },
  mixins: [Helper],
  props: {
    status: String,
  },
  data() {
    return {
      data: [],
      departmentList: [],
    }
  },
  emits: ['refresh-component-dept'],
  computed: {
    fields() {
      return [
        {
          key: 'name',
          inputName: 'unitName',
          label: this.$t('setting.list.employeeSetting.workUnit'),
          placeholder: this.$t('setting.list.employeeSetting.workUnit'),
          thClass: 'mc-table--50',
          isSortable: true,
          editable: true,
          validation: [['required']],
        },
        {
          name: 'department',
          key: 'department.name',
          label: this.$t('setting.list.employeeSetting.department'),
          thClass: 'mc-table--50',
          placeholder: this.$t('setting.list.employeeSetting.selectDepartment'),
          isEditable: true,
          type: 'select-resourceful',
          trackBy: 'structureId',
          optionsGetter: this.resourcefulQuery.department,
          optionLabelFn: ({ item }) => item.name,
          getInitialObject: (item) => item.department,
          customValueFn: (item) => item.department?.structureId ?? 0,
          thStyle: 'width: 300px',
          validation: [['required']],
        },
      ]
    },

    query() {
      return {
        get: async (filter) => {
          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_UNIT,
            variables: {
              filter: filter,
            },
          })
          let self = this
          self.data = res.data.units.Units.data

          let result = res.data.units.Units
          result.data.forEach((item, index) => {
            if (item.companyId == 1) {
              result.data[index].disableCheckbox = true
              result.data[index].isShowEdit = false
            } else if (item.hasRelation) {
              result.data[index].disableCheckbox = true
              result.data[index].isShowEdit = true
            }
          })

          return result
        },
        store: async (data) => {
          const res = await this.$apollo.mutate({
            mutation: UNIT_MUTATION_CREATE,
            variables: {
              input: {
                name: data.unitName,
                parentId: data['department.name'],
              },
            },
          })
          if (res.data.createUnit.message.toLowerCase().includes('unit name was exist')) {
            this.$mcToast.warning(this.$t('setting.list.employeeSetting.alert.workUnitExist'))
            return
          }
          let self = this
          let newData = res.data.createUnit.unit
          newData.hasRelation = false
          const department = this.departmentList.find(
            (item) => item.structureId == data['department.name']
          )
          if (department) {
            newData.department = {
              structureId: data['department.name'],
              name: department.name,
            }
          }
          self.data.push(newData)
          this.$emit('refresh-component-dept')
          return newData
        },
        update: async (id, oldData, newData) => {
          if (newData.unitName === undefined) newData.unitName = oldData.name
          if (newData['department.name'] === undefined)
            newData['department.name'] = oldData.department.structureId
          const res = await this.$apollo.mutate({
            mutation: UNIT_MUTATION_UPDATE,
            variables: {
              input: {
                oldName: oldData.name,
                name: newData.unitName,
                oldParentId: oldData.department.structureId ?? null,
                parentId: newData['department.name'],
              },
            },
          })
          if (res.data.updateUnit.message.toLowerCase().includes('unit name was exist')) {
            this.$mcToast.warning(this.$t('setting.list.employeeSetting.alert.workUnitExist'))
            return
          }
          let self = this
          let dataUpdateIndex = self.data.findIndex((item) => item.structureId == id)
          self.data[dataUpdateIndex].name = newData.unitName
          const department = this.departmentList.find(
            (item) => item.structureId == newData['department.name']
          )
          if (department) {
            self.data[dataUpdateIndex].department = {
              structureId: newData['department.name'],
              name: department.name,
            }
          }
          this.$emit('refresh-component-dept')
          return self.data[dataUpdateIndex]
        },
        delete: async (id) => {
          const res = await this.$apollo.mutate({
            mutation: DEPARTMENT_MUTATION_DELETE,
            variables: {
              input: {
                structureId: Number(id),
              },
            },
          })

          return res.data.deleteCompanyStructure.isSuccessful
        },
      }
    },

    resourcefulQuery() {
      return {
        department: async (opts) => {
          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
            level: [1],
            isPosition: false,
          }

          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_DEPARTMENT,
            variables: {
              filter: filter,
            },
          })

          const data = res.data.companyStructures.companyStructures
          const result = {
            metadata: {
              total_page: data.pagination.total,
              total_count: data.pagination.total,
              page: data.pagination.page,
            },
            data: {
              paginated_result: data.data,
            },
          }

          return result
        },
      }
    },
  },
  mounted() {
    this.$store.commit('setting/SET_STATUS', this.status)
  },
  methods: {
    async onClickTab() {
      this.$store.commit('setting/SET_STATUS', this.status)
      const res = await this.$apollo.query({
        query: GET_DEPARTMENT,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            level: [1],
          },
        },
      })
      this.departmentList = res.data.companyStructures.companyStructures.data
    },
  },
}
</script>

<template>
  <!-- Unit -->
  <b-tab class="border-0" @click="onClickTab">
    <template #title>
      <span class="d-sm-inline-block">{{ $t('setting.list.employeeSetting.workUnit') }}</span>
    </template>

    <!-- Table -->
    <div class="table-responsive my-3">
      <McGraphqlEditableTable
        :fields="fields"
        :apiClient="query"
        trackBy="structureId"
        :searchable="true"
      />
    </div>
  </b-tab>
</template>
